import pluralize from 'pluralize';
import { ModelCritiqueSeverity } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useModelCritique';
import { ExtendedModelCritiqueOutput } from 'react-scale/components/scalers/tasks/useModelCritiqueStore';
import { Icon, Typography, Button } from '@scale/scaleui';
import { Skeleton } from '@mui/material';
import { Transition } from '@headlessui/react';
import { memo, useMemo } from 'react';
import logger from '@scale/frontend-lib/logging';

const ModelCritiqueAlertUnmemoized = ({
  autoCritiqueHook,
  setAutoCritiqueValue,
}: {
  autoCritiqueHook: ExtendedModelCritiqueOutput;
  setAutoCritiqueValue: (value: ExtendedModelCritiqueOutput) => void;
}) => {
  const { severity, critique, loading, feedbackAcknowledged, opened } = autoCritiqueHook;

  const feedback = useMemo(() => {
    if (!loading) {
      return parseArray(critique);
    }
    return [];
  }, [loading]);

  if (severity === ModelCritiqueSeverity.Missing && loading) {
    return null;
  }

  const autoCritiqueToSeverityColor = (severity: ModelCritiqueSeverity) => {
    switch (severity) {
      case ModelCritiqueSeverity.Critical:
      case ModelCritiqueSeverity.Major:
        return 'danger';
      case ModelCritiqueSeverity.Moderate:
      case ModelCritiqueSeverity.Minor:
        return 'warning';
      case ModelCritiqueSeverity.None:
        return 'neutral';
    }
  };

  // Danger and warning border classes are missing in tailwind for some reason
  const patchMissingTailwindBorderClasses = (severity: ModelCritiqueSeverity) => {
    switch (severity) {
      case ModelCritiqueSeverity.Critical:
      case ModelCritiqueSeverity.Major:
        return 'border-[#9b4040]';
      case ModelCritiqueSeverity.Moderate:
      case ModelCritiqueSeverity.Minor:
        return 'border-[#ccc9b9]';
      case ModelCritiqueSeverity.None:
        return '';
    }
  };

  const ModelCritiqueSeverityMessage = (severity: ModelCritiqueSeverity, numIssues: number = 1) => {
    numIssues = Math.max(numIssues, 1);
    switch (severity) {
      case ModelCritiqueSeverity.Critical:
        return `This response almost certainly contains at least ${numIssues} ${pluralize(
          'issue',
          numIssues,
        )}.`;
      case ModelCritiqueSeverity.Major:
        return `This response most likely contains at least ${numIssues} ${pluralize(
          'issue',
          numIssues,
        )}.`;
      case ModelCritiqueSeverity.Moderate:
        return `This response likely contains at least ${numIssues} ${pluralize(
          'issue',
          numIssues,
        )}.`;
      case ModelCritiqueSeverity.Minor:
        return `This response may contain at least ${numIssues} ${pluralize('issue', numIssues)}.`;
      case ModelCritiqueSeverity.None:
        return `There may be no issues in this response. Double check to be sure.`;
    }
  };

  function parseArray(input: string): string[] {
    // The autocritique system always returns a format that is the severity, followed by a line break, followed by the feedback in an
    // array format. This function will attempt to split the first line which is the severity and parse the array.
    const array = input.split('\n').slice(1).join('\n').trim();

    // Try JSON parsing first
    try {
      return JSON.parse(array);
    } catch (jsonError) {
      logger.error('[ModelCritiqueAlert] JSON parse error:', jsonError);
    }

    // If JSON parsing fails because the model doesn't return the exact format we expect, we try plan B, which is using regex to parse
    // out any array that is found in the response.
    try {
      const regex = /\[\s*([^]]+)\s*\]/;
      const match = regex.exec(array);
      if (match) {
        // Include brackets in the match
        const matchedArray = match[0];
        return JSON.parse(matchedArray);
      }
    } catch (regexError) {
      logger.error('[ModelCritiqueAlert] Regex parse error:', regexError);
    }

    // If neither approach works, return an empty array
    return [];
  }

  return (
    <Transition
      show={!!critique}
      enter="transition-opacity duration-75"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-150"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div
        className={`scaleui rounded-md text-neutral-700 relative overflow-hidden p-4 mb-3 border border-${autoCritiqueToSeverityColor(
          severity,
        )}-600 ${patchMissingTailwindBorderClasses(severity)}`}
      >
        <div
          className={`bg-${autoCritiqueToSeverityColor(
            severity,
          )}-200 h-full w-full absolute top-0 left-0 opacity-50`}
        ></div>
        <div className="flex relative z-10">
          <div className="flex">
            <Icon
              className={`mr-2 mt-[-4px] text-${autoCritiqueToSeverityColor(severity)}-600`}
              icon={['fas', 'exclamation-triangle']}
              variant={autoCritiqueToSeverityColor(severity)}
            ></Icon>
            <div>
              <Typography.Subtitle2>
                {ModelCritiqueSeverityMessage(severity, feedback.length)}
              </Typography.Subtitle2>
              <div className="mt-2">
                This critique is based on an automatic analysis of the response.{' '}
                <b>
                  <u>You are ultimately responsible</u>
                </b>{' '}
                for ensuring the automated model critique system is accurate and its assessment is
                accurate.
              </div>

              <div hidden={!opened}>
                <hr className="my-3" hidden={severity === ModelCritiqueSeverity.None} />

                <ol className="list-disc">
                  {feedback.map((critique, index) => (
                    <div key={index} className="flex">
                      <li className="mt-1" style={{ lineHeight: 1.1 }} key={index}>
                        {critique}
                      </li>
                    </div>
                  ))}
                  <div className="mt-1" hidden={!loading}>
                    <Skeleton></Skeleton>
                    <Skeleton width="20ex"></Skeleton>
                  </div>
                </ol>
              </div>

              <hr className="my-3" />

              <div className="flex justify-between items-center">
                <div>
                  {feedbackAcknowledged ? (
                    <div>
                      <i>Your feedback has been submitted. Thank you!</i>
                    </div>
                  ) : (
                    <div className="flex items-center text-neutral-600 gap-1">
                      Was this feedback helpful?&nbsp; -&nbsp;&nbsp;
                      <Icon
                        className="pointer"
                        onClick={e => {
                          // This is just for user experience, it doesn't actually do anything
                          e.preventDefault();
                          setAutoCritiqueValue({
                            ...autoCritiqueHook,
                            feedbackAcknowledged: true,
                          });
                        }}
                        icon={['fas', 'thumbs-up']}
                      />
                      •
                      <Icon
                        className="cursor-pointer pt-1"
                        onClick={e => {
                          // This is just for user experience, it doesn't actually do anything
                          e.preventDefault();
                          setAutoCritiqueValue({
                            ...autoCritiqueHook,
                            feedbackAcknowledged: true,
                          });
                        }}
                        icon={['fas', 'thumbs-down']}
                      />
                    </div>
                  )}
                </div>

                <div hidden={!feedback.length}>
                  <Button
                    onClick={e => {
                      e.preventDefault();
                      setAutoCritiqueValue({
                        ...autoCritiqueHook,
                        opened: !opened,
                      });
                    }}
                    variant={autoCritiqueToSeverityColor(severity)}
                  >
                    <Icon
                      size={'sm'}
                      variant={'primary'}
                      icon={['fas', opened ? 'eye-slash' : 'eye']}
                    ></Icon>
                    <span className="text-primary-700">
                      {opened ? 'Hide' : 'Show'} {feedback.length}{' '}
                      {pluralize('issue', feedback.length)}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};

export const ModelCritiqueAlert = memo(ModelCritiqueAlertUnmemoized);
